import {Injectable} from '@angular/core';
import {TranslateService as NgxTranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {TranslateKey} from '../types/translate-key';

/**
 * Enumeration of all languages supported by the application
 * @enum
 */
export type SupportedLangs = 'fr' | 'en';

/**
 * Provides all logic related to application's translation
 *
 * When the application init, this service will process the following:
 * * Adds List of langs available (french & english for now)
 * * Checks browser language preference
 * * Sets current language based on stored language or browser language
 *
 * @author louiiuol
 */
@Injectable({providedIn: 'root'})
export class TranslateService {
	currentLang!: SupportedLangs;
	onLangChange = this.translator.onLangChange;

	/**
	 * Current language active. The value is updated whenever `use(lang)` is called
	 */

	constructor(private translator: NgxTranslateService) {
		this.translator.onLangChange.subscribe(e => {
			this.currentLang = e.lang as SupportedLangs;
		});
	}

	/**
	 * Initializes available languages and loads the active language's
	 * translation file.
	 *
	 * Returns a promise that resolves once the translations are fully loaded,
	 * so it can be awaited in an `APP_INITIALIZER`. Awaiting it prevents raw
	 * translation keys from being displayed on slow connections (the app waits
	 * for the JSON instead of rendering before `instant()` has any data).
	 */
	init(): Promise<unknown> {
		const supported: SupportedLangs[] = ['fr', 'en'];
		this.translator.addLangs(supported);
		this.translator.setDefaultLang('fr');

		// Try saved language first
		const savedLang = localStorage.getItem('lang') as SupportedLangs;

		// Fallback to browser language
		const browserLang = this.translator.getBrowserLang() as SupportedLangs;
		const langToUse =
			savedLang && supported.includes(savedLang)
				? savedLang
				: browserLang && supported.includes(browserLang)
				? browserLang
				: 'fr';

		this.currentLang = langToUse;
		return firstValueFrom(this.translator.use(langToUse));
	}

	/**
	 * Set current language to given lang.
	 * This action will also refresh the page in order to fully translate the page
	 * @param lang language to use
	 */
	use = (lang: SupportedLangs) => {
		if (lang !== this.currentLang) {
			this.currentLang = lang;
			this.translator.use(lang);
			localStorage.setItem('lang', lang);
		}
	};

	/**
	 * Retrieves all langs available for the application
	 * @returns all langs available for the application
	 */
	getLangs = () => this.translator.getLangs() as SupportedLangs[];

	/**
	 * Translate given key, with current lang active.
	 *
	 * Make sure the key matches a path inside translation files.
	 * If you want to use params inside translation please follow this guide
	 *
	 * @see {@link https://www.vitamindev.com/angular/how-to-use-parameters-in-ngx-translate/ | how-to-use-parameters-in-ngx-translate }
	 * @param key path of the translation
	 * @param params params to interpolate inside translated message
	 * @returns translated message or given key if no translation was found
	 */
	translate(key?: string | TranslateKey | null): string {
		if (!key) return '';
		return this.isKeyWithParam(key)
			? this.translator.instant(key.key, key.param)
			: this.translator.instant(key);
	}

	/**
	 * Translate given key, with current lang active, and wraps it into an observable.
	 *
	 * Make sure the key matches a path inside translation files.
	 * If you want to use params inside translation please follow this guide
	 *
	 * @see {@link https://www.vitamindev.com/angular/how-to-use-parameters-in-ngx-translate/ | how-to-use-parameters-in-ngx-translate }
	 * @param key path of the translation
	 * @param params params to interpolate inside translated message
	 * @returns Observable containing translated message or given key if no translation was found
	 */
	stream = (key: string | string[], params?: object | undefined) =>
		this.translator.stream(key, params);

	private isKeyWithParam(
		tk: TranslateKey | string
	): tk is {key: string; param: Record<string, any>} {
		return typeof tk === 'object' && tk !== null && 'key' in tk;
	}

	setUserLanguage(lang?: string) {
		const supported: SupportedLangs[] = ['fr', 'en'];

		if (lang && supported.includes(lang as SupportedLangs)) {
			this.use(lang as SupportedLangs);
		}
	}
}
